import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, LogOut, Menu, X, ShieldCheck, ChevronDown, Instagram } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth/AuthContext";
import { Logo } from "@/components/Logo";

const INSTAGRAM_URL = "https://www.instagram.com/lumin_africa/";

const RUBRIQUES = [
  { to: "/mannequins", key: "model" },
  { to: "/photographes", key: "photographer" },
  { to: "/stylistes", key: "stylist" },
  { to: "/marques", key: "brand" },
  { to: "/boutiques", key: "boutiques" },
  { to: "/evenements", key: "events" },
];

export const Navbar = () => {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  const rubLabel = (key) => (key === "all" ? t.nav.allRubriques : key === "boutiques" ? t.nav.boutiques : t.catalogue.cat[key]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const doLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/", { replace: true });
  };

  const isAuthed = user && user !== null;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 z-50 w-full transition-all duration-500 ${
        scrolled || open
          ? "backdrop-blur-xl bg-[#0d0d0d]/80 border-b border-[#d4af37]/20 shadow-[0_8px_30px_rgba(0,0,0,0.5)] py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
        <Logo />

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-5 lg:gap-8">
          <div
            className="relative"
            onMouseEnter={() => setCatOpen(true)}
            onMouseLeave={() => setCatOpen(false)}
          >
            <button
              data-testid="nav-catalog-btn"
              onClick={() => setCatOpen(true)}
              className="flex items-center gap-1.5 text-sm tracking-wide text-white/80 hover:text-white transition-colors"
            >
              {t.nav.catalog} <ChevronDown size={14} className={`transition-transform ${catOpen ? "rotate-180" : ""}`} />
            </button>
            {catOpen && (
              <div
                data-testid="nav-catalog-menu"
                className="absolute left-0 top-full pt-3 w-56"
              >
                <div className="bg-[#0d0d0d] border border-white/10 shadow-2xl py-2">
                  {RUBRIQUES.map((r) => (
                    <Link
                      key={r.to}
                      to={r.to}
                      data-testid={`nav-rub-${r.key}`}
                      onClick={() => setCatOpen(false)}
                      className="block px-5 py-2.5 text-sm text-white/75 hover:text-[#d4af37] hover:bg-white/[0.03] transition-colors"
                    >
                      {rubLabel(r.key)}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {isAuthed ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin-btn" className="flex items-center gap-1.5 text-sm tracking-wide text-[#d4af37] hover:text-[#f3e5ab] transition-colors"><LayoutDashboard size={15} /> Super Admin</Link>
              )}
              <Link to="/dashboard" data-testid="nav-dashboard-btn" className="flex items-center gap-2 text-sm tracking-wide text-white/80 hover:text-white transition-colors"><LayoutDashboard size={15} /> {user.name?.split(" ")[0] || t.nav.catalog}</Link>
              <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="nav-instagram" className="text-white/70 hover:text-[#d4af37] transition-colors"><Instagram size={18} /></a>
              <button onClick={doLogout} data-testid="nav-logout-btn" className="flex items-center gap-2 text-sm tracking-wide px-6 py-2.5 border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-all duration-300"><LogOut size={15} /> {t.dash.logout}</button>
            </>
          ) : (
            <>
              <Link to="/register?role=brand" data-testid="nav-brand-cta" className="text-sm tracking-wide text-white/80 hover:text-[#d4af37] transition-colors">{t.nav.brandCta}</Link>
              <Link to="/login" data-testid="nav-login-btn" className="text-sm tracking-wide text-white/80 hover:text-white transition-colors">{t.nav.login}</Link>
              <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="nav-instagram" className="text-white/70 hover:text-[#d4af37] transition-colors"><Instagram size={18} /></a>
              <Link to="/register" data-testid="nav-join-btn" className="text-sm tracking-wide px-6 py-2.5 border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-black transition-all duration-300">{t.nav.join}</Link>
            </>
          )}
        </div>

        {/* Mobile trigger */}
        <div className="flex md:hidden items-center gap-4">
          <button onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label="Menu" className="text-white p-1">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden mt-4 px-6 pb-4 flex flex-col gap-1 border-t border-white/10 pt-4">
          <p className="text-[0.65rem] uppercase tracking-[0.25em] text-white/35 mt-1 mb-1">{t.nav.catalog}</p>
          {RUBRIQUES.map((r) => (
            <Link key={r.to} to={r.to} onClick={() => setOpen(false)} data-testid={`m-nav-rub-${r.key}`} className="py-2.5 pl-3 text-white/80 hover:text-[#d4af37] transition-colors">
              {rubLabel(r.key)}
            </Link>
          ))}
          <div className="h-px bg-white/10 my-2" />
          <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} data-testid="m-nav-instagram" className="py-3 flex items-center gap-2 text-white/80 hover:text-[#d4af37] transition-colors"><Instagram size={16} /> Instagram</a>
          {isAuthed ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setOpen(false)} data-testid="m-nav-admin" className="py-3 flex items-center gap-2 text-[#d4af37]"><ShieldCheck size={16} /> Super Admin</Link>
              )}
              <Link to="/dashboard" onClick={() => setOpen(false)} data-testid="m-nav-dashboard" className="py-3 flex items-center gap-2 text-white/80 hover:text-[#d4af37] transition-colors"><LayoutDashboard size={16} /> {t.dash.profileTab}</Link>
              <button onClick={doLogout} data-testid="m-nav-logout" className="mt-2 py-3 flex items-center justify-center gap-2 border border-[#d4af37] text-[#d4af37]"><LogOut size={16} /> {t.dash.logout}</button>
            </>
          ) : (
            <>
              <Link to="/register?role=brand" onClick={() => setOpen(false)} data-testid="m-nav-brand-cta" className="py-3 text-[#d4af37] hover:text-[#f3e5ab] transition-colors">{t.nav.brandCta}</Link>
              <Link to="/login" onClick={() => setOpen(false)} data-testid="m-nav-login" className="py-3 text-white/80 hover:text-[#d4af37] transition-colors">{t.nav.login}</Link>
              <Link to="/register" onClick={() => setOpen(false)} data-testid="m-nav-join" className="mt-2 py-3 flex items-center justify-center border border-[#d4af37] text-[#d4af37]">{t.nav.join}</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};
